/**
 * utils/apiErrorText.ts
 *
 * Pulls human-readable text out of a backend error body.
 *
 * The API wraps every failure as `{ code, data }` (see crhub.utils.on_fail), but
 * `data` is not one shape: DRF ValidationError details arrive as an array of
 * strings, other 400s send a plain string, and network failures leave it null or
 * an object. Calling `.join(' ')` blindly throws a TypeError that swallows the
 * real reason and reports "data.join is not a function" instead — so every caller
 * must go through this helper.
 */

/** Best-effort human-readable text out of an error response body ({ code, data }). */
export function errorTextFromResponse(response: unknown, fallback: string): string {
  const data = (response as { data?: unknown } | null | undefined)?.data;
  if (Array.isArray(data)) {
    const joined = data.filter((item) => typeof item === 'string').join(' ');
    return joined || fallback;
  }
  if (typeof data === 'string' && data) return data;
  // Nested object bodies (e.g. the name-conflict payload { message, size, ... }).
  if (data && typeof data === 'object') {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return fallback;
}
